import { useEffect, useState } from "react";
import api from "@/utils/axios-utils";
import { useAuthStore } from "@/store/useAuthStore";
import { ACCOUNTS_CACHE_KEY } from "@/constants/auth";

export interface AccessibleAccount {
  teacher_id: number;
  user_id: number;
  name: string | null;
  username: string | null;
  email: string | null;
  is_you: boolean;
  has_analytics: boolean;
}

/**
 * An account's primary display name, with a stable fallback when name, username
 * and email are all unset. Callers that render the username separately (the
 * account switcher, which gives it its own line) want this; callers with one
 * line to work with want `accountLabel`.
 */
export const accountName = (account: AccessibleAccount): string =>
  account.name?.trim() ||
  account.username?.trim() ||
  account.email ||
  `Teacher ${account.teacher_id}`;

/**
 * Single-line label for an account: `Name (username)`, since an org's teachers
 * are distinguishable by login username but not always by name. Drops the
 * parenthetical when it would only repeat the name it follows.
 */
export const accountLabel = (account: AccessibleAccount): string => {
  const name = accountName(account);
  const username = account.username?.trim();
  return username && username !== name ? `${name} (${username})` : name;
};

/**
 * The user_ids worth offering in the teacher picker: those with analytics
 * activity behind them. `has_analytics` is an "is there data" flag, not a
 * permission — a teacher the viewer may absolutely see is still excluded here
 * until they have their first session. Use `accessibleTeacherUserIds` for any
 * question about access.
 */
export const selectableTeacherUserIds = (
  accounts: AccessibleAccount[],
): Set<number> =>
  new Set(accounts.filter((a) => a.has_analytics).map((a) => a.user_id));

/**
 * The user_ids the viewer may scope analytics to at all. Every account the
 * endpoint returns is one the viewer may act as, so presence is the permission;
 * whether that teacher has any data yet is a separate question. Scoping to an
 * accessible teacher with no activity correctly yields an empty view of *that
 * teacher* rather than a populated view of somebody else.
 */
export const accessibleTeacherUserIds = (
  accounts: AccessibleAccount[],
): Set<number> => new Set(accounts.map((a) => a.user_id));

/**
 * How a URL-requested set of teacher ids stands against the viewer's accessible
 * accounts.
 *
 * Filtering requested ids through `selectableTeacherUserIds` collapses four
 * very different situations into one empty array: nothing has loaded yet, the
 * lookup failed, the viewer genuinely may not see those teachers, or the URL
 * asked for no teacher at all. Only the last of those makes a default scope
 * (the viewer's own students) the right answer — in the other three, quietly
 * falling back would label one teacher's analytics with another's name. This
 * predicate keeps them apart so a caller can hold, report, or proceed.
 */
export type TeacherRequestState =
  /** The URL requested no teacher; the viewer's default scope is correct. */
  | "none"
  /** Accounts haven't loaded yet, so the request can't be judged — hold. */
  | "pending"
  /** The accounts lookup failed, so the request can't be judged, and won't be. */
  | "unresolvable"
  /** Accounts resolved and none of the requested ids are viewable. */
  | "denied"
  /** At least one requested id is viewable. */
  | "granted";

export const teacherRequestState = (
  requestedTeacherIds: number[],
  accounts: AccessibleAccount[],
  isLoadingAccounts: boolean,
  accountsError: unknown,
): TeacherRequestState => {
  if (requestedTeacherIds.length === 0) return "none";
  // Checked before `pending`: a failed lookup clears accounts and stops
  // loading, so it is otherwise indistinguishable from a resolved denial.
  if (accountsError != null) return "unresolvable";
  // Any list at all, cached or fresh, is enough to judge against — a warm
  // cache's background revalidation must not re-gate an already-correct view.
  if (isLoadingAccounts && accounts.length === 0) return "pending";
  const accessible = accessibleTeacherUserIds(accounts);
  return requestedTeacherIds.some((id) => accessible.has(id))
    ? "granted"
    : "denied";
};

// Switching accounts triggers a full page reload (see useAuthStore.setActingTeacher).
// Caching the accessible set lets the switcher render immediately on the next mount
// instead of flickering away until /accounts/accessible re-resolves.

const readCachedAccounts = (): AccessibleAccount[] => {
  try {
    const raw = localStorage.getItem(ACCOUNTS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Fetches the accounts the current user may view-as (own account + co-teach
 * targets, or all org teachers for an org admin). Backed by useState/useEffect
 * since this frontend has no query layer; seeds from a localStorage cache to
 * survive the reload that an account switch performs.
 */
export const useAccounts = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [accounts, setAccounts] =
    useState<AccessibleAccount[]>(readCachedAccounts);
  // True only while there is no cached answer yet, so a consumer gating a
  // request on this flag can tell "haven't checked" apart from "authenticated
  // user genuinely has zero accounts" — both look like an empty accounts
  // array, but only the first should hold a request back.
  const [isLoading, setIsLoading] = useState(() => accounts.length === 0);
  // Set only when the accessible set is genuinely unknown. An empty `accounts`
  // is otherwise ambiguous — "you may view no one else" and "we never found
  // out" look identical — and a caller resolving a URL-requested teacher has
  // to tell those apart to avoid silently substituting a different scope.
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setAccounts([]);
      localStorage.removeItem(ACCOUNTS_CACHE_KEY);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    api
      .get<AccessibleAccount[]>("/accounts/accessible")
      .then((res) => {
        if (cancelled) return;
        const next = res.data ?? [];
        setAccounts(next);
        setError(null);
        localStorage.setItem(ACCOUNTS_CACHE_KEY, JSON.stringify(next));
      })
      .catch((err) => {
        // A plain teacher with no co-teach links 404s/403s or returns just
        // themselves — either way, no switcher. Fail closed to empty. Auth
        // errors are expected; log anything else so an unexpected failure
        // (5xx, network) isn't swallowed silently.
        if (cancelled) return;
        const statusCode = err?.response?.status;
        const isExpected =
          statusCode === 401 || statusCode === 403 || statusCode === 404;
        if (!isExpected) {
          console.error("Failed to load accessible accounts", err);
        }
        // An expected rejection is a real answer — "no accessible accounts" —
        // so it resolves rather than errors. A 5xx or network failure answers
        // nothing, and must not be mistaken for one.
        setError(isExpected ? null : err);
        setAccounts([]);
        localStorage.removeItem(ACCOUNTS_CACHE_KEY);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return { accounts, isLoading, error };
};
